<template>
  <section class="ncr-files-pane" @dragover.prevent @drop.prevent="handleDrop">
    <button v-if="!readonly" type="button" class="ncr-files-dropzone" :disabled="!recordId || isUploading"
      @click="uploadInput?.click()">
      <i class="material-icons">attach_file</i>
      <span>Drag and Drop or <strong>Choose File</strong></span>
      <small>Images, PDF, XLSX, CSV, or TXT. Maximum 10 MB.</small>
    </button>
    <input ref="uploadInput" type="file" class="ncr-files-hidden-input" multiple hidden @change="handleInputChange" />

    <div v-if="isUploading" class="ncr-file-progress">
      <span>{{ uploadLabel }}</span>
      <div><span :style="{ width: `${uploadProgress}%` }"></span></div>
    </div>
    <div v-if="errorMessage" class="ncr-pane-alert is-error">{{ errorMessage }}</div>

    <div v-if="!modernFiles.length && !visibleLegacyFiles.length" class="ncr-pane-empty">No files attached yet.</div>
    <div v-else class="ncr-files-list">
      <article v-for="file in modernFiles" :key="file.id" class="ncr-file-row">
        <span class="ncr-file-type">{{ file.extension.toUpperCase() || 'FILE' }}</span>
        <button type="button" class="ncr-file-name" @click="downloadModern(file)">{{ file.displayName }}</button>
        <small>v{{ file.version }}</small>
        <button v-if="!readonly" type="button" class="button pill -text" aria-label="Delete file"
          @click="removeModern(file)">
          <i class="material-icons">delete</i>
        </button>
      </article>
      <article v-for="file in visibleLegacyFiles" :key="`${file.name}-${file.path}`" class="ncr-file-row is-legacy">
        <span class="ncr-file-type">FILE</span>
        <a class="ncr-file-name" :href="file.path" target="_blank" rel="noopener">{{ file.name }}</a>
        <small>Legacy</small>
        <button v-if="!readonly" type="button" class="button pill -text" aria-label="Delete legacy file"
          @click="$emit('remove-legacy', file)">
          <i class="material-icons">delete</i>
        </button>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import { collection, onSnapshot, orderBy, query, type Unsubscribe } from 'firebase/firestore';
import { computed, onBeforeUnmount, ref, watch } from 'vue';

import { db } from '@/modules/firebase';
import { fileClient, type RecordFile } from '@/modules/common/files/FileClient';
import {
  uploadRecordFiles,
  validateLocalRecordFile
} from '@/modules/common/files/RecordFileWorkflow';
import type { LegacyNcrFile } from '@/modules/ncr/NcrClient';
import { ncrClient } from '@/modules/ncr/NcrClient';

const props = defineProps<{
  recordId: string;
  legacyFiles: LegacyNcrFile[];
  readonly?: boolean;
}>();

defineEmits<{
  'remove-legacy': [file: LegacyNcrFile];
}>();

const modernFiles = ref<RecordFile[]>([]);
const uploadInput = ref<HTMLInputElement | null>(null);
const isUploading = ref(false);
const uploadProgress = ref(0);
const uploadLabel = ref('');
const errorMessage = ref('');
let unsubscribe: Unsubscribe | null = null;
const visibleLegacyFiles = computed(() => props.legacyFiles.filter(file => !file.sourceFileId));

function timestampToMillis(value: any): number {
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  return typeof value === 'number' ? value : 0;
}

function subscribe(recordId: string): void {
  unsubscribe?.();
  unsubscribe = null;
  modernFiles.value = [];
  if (!recordId) return;

  unsubscribe = onSnapshot(
    query(collection(db, 'ncr', recordId, 'files'), orderBy('createdAt', 'desc')),
    snapshot => {
      modernFiles.value = snapshot.docs
        .map(entry => {
          const value = entry.data();
          return {
            id: entry.id,
            ...value,
            createdAt: timestampToMillis(value.createdAt),
            updatedAt: timestampToMillis(value.updatedAt)
          } as RecordFile;
        })
        .filter(file => !file.deleted && file.status !== 'deleted');
    },
    error => {
      errorMessage.value = error instanceof Error ? error.message : 'Failed to load files';
    }
  );
}

async function upload(files: File[]): Promise<void> {
  if (props.readonly || !props.recordId || !files.length || isUploading.value) return;
  for (const file of files) {
    const validationError = validateLocalRecordFile(file);
    if (validationError) {
      errorMessage.value = validationError;
      return;
    }
  }

  isUploading.value = true;
  errorMessage.value = '';
  uploadProgress.value = 0;
  try {
    const uploadedFiles = await uploadRecordFiles({
      recordType: 'ncr',
      recordId: props.recordId,
      files,
      onProgress: progress => {
        uploadProgress.value = progress.percent;
        uploadLabel.value = `Uploading ${progress.fileName} (${progress.percent}%)`;
      }
    });
    for (const uploadedFile of uploadedFiles) {
      const downloadUrl = await fileClient.getDownloadUrl(uploadedFile.storagePath);
      await ncrClient.addModernFileMirror(props.recordId, uploadedFile, downloadUrl);
    }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'File upload failed';
  } finally {
    isUploading.value = false;
    uploadLabel.value = '';
    if (uploadInput.value) uploadInput.value.value = '';
  }
}

function handleInputChange(event: Event): void {
  const files = Array.from((event.target as HTMLInputElement).files || []);
  void upload(files);
}

function handleDrop(event: DragEvent): void {
  void upload(Array.from(event.dataTransfer?.files || []));
}

async function downloadModern(file: RecordFile): Promise<void> {
  try {
    const url = await fileClient.getDownloadUrl(file.storagePath);
    window.open(url, '_blank', 'noopener');
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to open file';
  }
}

async function removeModern(file: RecordFile): Promise<void> {
  if (!window.confirm(`Delete ${file.displayName}?`)) return;
  try {
    await fileClient.deleteFile('ncr', props.recordId, file.id);
    await ncrClient.removeModernFileMirror(props.recordId, file.id, props.legacyFiles);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to delete file';
  }
}

watch(() => props.recordId, subscribe, { immediate: true });
onBeforeUnmount(() => unsubscribe?.());
</script>
